import { AlbumGrid } from "@/components/album-grid";
import { TrackingScope } from "@/components/TrackingScope";
import { FeaturePageAlbumTags } from "@/features/feature-page/feature-page-album-tags";
import { useFeaturePageCatalog } from "@/features/feature-page/feature-page-catalog-context";
import { FeaturePageEntityCard } from "@/features/feature-page/feature-page-entity-card";
import { FeaturePageSection } from "@/features/feature-page/feature-page-section";
import type { ResolvedFeaturePageBlock } from "@garimpa/api/featurePages/types";

import { toFeaturePageAlbumGridItem } from "./feature-page-album-grid-item";
import { isFeaturePageAlbumEntity } from "./is-feature-page-album-entity";

const centeredGridItemClassName =
  "w-[calc((100%-1rem)/2)] sm:w-[calc((100%-2rem)/3)] lg:w-[calc((100%-3rem)/4)] xl:w-[calc((100%-5rem)/6)]";

export function EntityCollectionBlock({
  block,
}: {
  block: Extract<ResolvedFeaturePageBlock, { type: "entityCollection" }>;
}) {
  const catalog = useFeaturePageCatalog();
  const items = (block.resolvedItemRefs ?? []).flatMap((ref) => {
    const entity = catalog.getEntity(ref);
    return entity ? [{ entity, catalogRefId: ref.catalogRefId }] : [];
  });
  if (!items.length) return null;
  const albumItems = items.flatMap(({ entity, catalogRefId }) => {
    if (!isFeaturePageAlbumEntity(entity)) return [];
    const album = toFeaturePageAlbumGridItem({ entity });
    return album
      ? [
          {
            ...album,
            annotation: catalogRefId ? block.annotations?.[catalogRefId] : undefined,
          },
        ]
      : [];
  });
  const isAlbumCollection = block.entityType === "work" || block.entityType === "release";
  const isRail = block.variant === "rail";
  const layoutClassName = isRail
    ? "flex w-max min-w-full items-stretch justify-center gap-4"
    : "flex flex-wrap justify-center gap-4";
  const itemClassName = isRail
    ? "h-auto w-52 shrink-0 snap-start sm:w-64"
    : centeredGridItemClassName;
  const collection = isAlbumCollection ? (
    <TrackingScope entityType={block.entityType} surface="feature_page">
      <AlbumGrid
        albums={albumItems}
        className={layoutClassName}
        footer={(album) =>
          album.annotation?.note ? (
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {album.annotation.note}
            </p>
          ) : null
        }
        itemClassName={itemClassName}
        itemKey={(album) => `${album.entity.entityType}-${album.entity.id}`}
        link={(album) => album.link}
        linkCoversFooter
        showAlbumType={false}
        showOfferSummary={false}
        tags={(album) => <FeaturePageAlbumTags values={[album.annotation?.badge]} />}
      />
    </TrackingScope>
  ) : (
    <ul className={layoutClassName}>
      {items.map(({ entity, catalogRefId }) => {
        const annotation = catalogRefId ? block.annotations?.[catalogRefId] : undefined;
        return (
          <li key={`${entity.entityType}-${entity.id}`} className={itemClassName}>
            <FeaturePageEntityCard
              entity={entity}
              badge={annotation?.badge}
              note={annotation?.note}
            />
          </li>
        );
      })}
    </ul>
  );
  return (
    <FeaturePageSection block={block} resolvedBackground={block.resolvedBackground}>
      {block.heading ? (
        <h2
          id={`${block.id}-heading`}
          className="mb-10 text-center text-3xl font-bold tracking-tight sm:text-5xl"
        >
          {block.heading}
        </h2>
      ) : null}
      {isRail ? (
        <div className="snap-x overflow-x-auto pb-4 [scrollbar-width:thin]">{collection}</div>
      ) : (
        collection
      )}
    </FeaturePageSection>
  );
}
